import { Link } from 'react-router-dom'
import { ArrowRight, ChevronRight, Lock, type LucideIcon } from 'lucide-react'

// Full class strings so Tailwind keeps them
const ACCENTS = {
  blue: {
    tile: 'bg-blue-600 shadow-blue-200',
    surface: 'from-blue-50',
    text: 'text-blue-700',
    border: 'hover:border-blue-300',
    button: 'bg-blue-600 hover:bg-blue-700',
  },
  green: {
    tile: 'bg-green-600 shadow-green-200',
    surface: 'from-green-50',
    text: 'text-green-700',
    border: 'hover:border-green-300',
    button: 'bg-green-600 hover:bg-green-700',
  },
  amber: {
    tile: 'bg-amber-500 shadow-amber-200',
    surface: 'from-amber-50',
    text: 'text-amber-700',
    border: 'hover:border-amber-300',
    button: 'bg-amber-500 hover:bg-amber-600',
  },
  purple: {
    tile: 'bg-purple-600 shadow-purple-200',
    surface: 'from-purple-50',
    text: 'text-purple-700',
    border: 'hover:border-purple-300',
    button: 'bg-purple-600 hover:bg-purple-700',
  },
  indigo: {
    tile: 'bg-indigo-600 shadow-indigo-200',
    surface: 'from-indigo-50',
    text: 'text-indigo-700',
    border: 'hover:border-indigo-300',
    button: 'bg-indigo-600 hover:bg-indigo-700',
  },
} as const

export interface DashboardFeature {
  id: string
  title: string
  description: string
  path: string
  icon: LucideIcon
  accent: keyof typeof ACCENTS
  /** Action label on the card, e.g. "Go to Test Series" (default "Open") */
  action?: string
}

interface FeatureCardProps {
  feature: DashboardFeature
  /** Short live figure, e.g. "Avg accuracy 62%" */
  stat?: string
  /** Wide, prominent card for the main feature */
  hero?: boolean
  /** Locked cards show a lock and call onLockedClick instead of navigating */
  locked?: boolean
  onLockedClick?: (feature: DashboardFeature) => void
}

export default function FeatureCard({ feature, stat, hero = false, locked = false, onLockedClick }: FeatureCardProps) {
  const Icon = feature.icon
  const accent = ACCENTS[feature.accent]
  const action = locked ? 'Unlock' : feature.action ?? 'Open'

  const iconTile = (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-xl ${
        hero ? 'h-14 w-14' : 'h-11 w-11 sm:h-12 sm:w-12'
      } ${locked ? 'bg-gray-100 text-gray-400' : `${accent.tile} text-white shadow-md`}`}
    >
      {locked ? <Lock className="h-5 w-5" /> : <Icon className={hero ? 'h-7 w-7' : 'h-5 w-5 sm:h-6 sm:w-6'} />}
    </div>
  )

  const statChip = stat && !locked && (
    <span className={`inline-flex rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ring-gray-200 ${accent.text}`}>
      {stat}
    </span>
  )

  const content = hero ? (
    // Hero: horizontal on tablet/desktop, stacked on phones
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      {iconTile}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-bold text-gray-900 sm:text-xl">{feature.title}</h3>
          {statChip}
        </div>
        <p className="text-sm text-gray-600">{feature.description}</p>
      </div>
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors ${accent.button}`}
      >
        {action}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </div>
  ) : (
    // Phones: compact row. Tablet/desktop: card
    <div className="flex items-center gap-3 sm:block">
      <div className="sm:mb-4">{iconTile}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:mb-1">
          <h3 className={`font-semibold ${locked ? 'text-gray-700' : 'text-gray-900'}`}>{feature.title}</h3>
          {statChip}
        </div>
        <p className="line-clamp-1 text-sm text-gray-500 sm:line-clamp-none">{feature.description}</p>
        <span
          className={`mt-4 hidden items-center gap-1 text-sm font-semibold sm:inline-flex ${
            locked ? 'text-gray-500' : accent.text
          }`}
        >
          {action}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
      <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-300 sm:hidden" />
    </div>
  )

  const className = `group block w-full rounded-xl border border-gray-200 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
    hero ? 'p-5 sm:p-6' : 'p-4 sm:p-5'
  } ${locked ? 'bg-white hover:border-gray-300' : `bg-gradient-to-br ${accent.surface} to-white ${accent.border}`}`

  return locked ? (
    <button type="button" onClick={() => onLockedClick?.(feature)} className={className}>
      {content}
    </button>
  ) : (
    <Link to={feature.path} className={className}>
      {content}
    </Link>
  )
}
